import React from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import {
  styles,
  Row,
  Table,
  TextCell,
  safeZero,
  safeString,
} from "./reportingHelpers";
import { IntlProvider } from "react-intl";
import {
  FixtureReportsHeader,
  ScoringTable,
  parseScoringIntervals,
} from "./FixtureReportsHeader"; //import fibaLogo from './fibaLogo.png';
import { FixtureReportsFooter } from "./FixtureReportsFooter";

let players = {};
let homeTeamQuarters = {};
let awayTeamQuarters = {};

let periodName = "";
let overtimeName = "";

const periodLabel = (periodId) => {
  if (periodId < 5) {
    return periodName + periodId.toString();
  } else {
    return overtimeName + (periodId - 10).toString();
  }
};

export const QuarterScoresReport = (props) => {
  const { language, locale, intl } = props;
  const {
    teams,
    fixture,
    scoringIntervals,
    roles,
    fixtureProfile: { profile: fixtureProfile },
  } = props.data;
  let homeTeamPeriods,
    awayTeamPeriods = [];
  let periodsScores = [];

  const teamIds = Object.keys(teams);
  const homeTeamId = teams[teamIds[0]].isHome ? teamIds[0] : teamIds[1];
  const awayTeamId = teams[teamIds[0]].isHome ? teamIds[1] : teamIds[0];

  const homeTeam = { ...teams[homeTeamId], entityId: homeTeamId };
  const awayTeam = { ...teams[awayTeamId], entityId: awayTeamId };

  const periodIds = [
    ...new Set(props.data.playByPlay.map((play) => play.periodId)),
  ];

  if (homeTeam && awayTeam && scoringIntervals) {
    homeTeamPeriods = parseScoringIntervals(homeTeam, scoringIntervals, intl);
    awayTeamPeriods = parseScoringIntervals(awayTeam, scoringIntervals, intl);

    let lastHomeScore = 0,
      lastAwayScore = 0;
    homeTeamPeriods.forEach((period, index) => {
      const awayPeriod = awayTeamPeriods[index];
      const newHomeScore = period.interval2 || period.interval1;
      const newAwayScore = awayPeriod.interval2 || awayPeriod.interval1;
      periodsScores.push(
        `${newHomeScore - lastHomeScore}-${newAwayScore - lastAwayScore}`
      );
      lastHomeScore = newHomeScore;
      lastAwayScore = newAwayScore;
    });

    homeTeam["score"] = lastHomeScore;
    awayTeam["score"] = lastAwayScore;

    const QuarterScoresRows = [...props.data.playByPlay];

    homeTeam.players.forEach((player) => {
      player["homeTeam"] = true;
      players[player.personId] = player;
    });
    awayTeam.players.forEach((player) => {
      player["homeTeam"] = false;
      players[player.personId] = player;
    });

    const playScoring = {
      "2pt": 2,
      "3pt": 3,
      freeThrow: 1,
    };

    Object.keys(players).forEach((playerId) => {
      const playByPlayRows = QuarterScoresRows.filter(
        (event) => event.success && event.personId === playerId
      );

      if (!players[playerId]["total"]) {
        players[playerId]["total"] = 0;
      }
      if (!homeTeamQuarters["total"]) {
        homeTeamQuarters["total"] = 0;
      }
      if (!awayTeamQuarters["total"]) {
        awayTeamQuarters["total"] = 0;
      }

      periodIds.forEach((periodId) => {
        if (!players[playerId]["period" + periodId]) {
          players[playerId]["period" + periodId] = 0;
        }
        if (!homeTeamQuarters["period" + periodId]) {
          homeTeamQuarters["period" + periodId] = 0;
        }
        if (!awayTeamQuarters["period" + periodId]) {
          awayTeamQuarters["period" + periodId] = 0;
        }
      });

      playByPlayRows.forEach((playByPlay) => {
        players[playerId]["period" + playByPlay.periodId] +=
          playScoring[playByPlay.eventType];
        players[playerId]["total"] += playScoring[playByPlay.eventType];

        const teamTotals = players[playerId]["homeTeam"]
          ? homeTeamQuarters
          : awayTeamQuarters;
        teamTotals["period" + playByPlay.periodId] +=
          playScoring[playByPlay.eventType];
        teamTotals["total"] += playScoring[playByPlay.eventType];
      });
    });

    let homeRunningTotal = 0,
      awayRunningTotal = 0;
    periodIds.forEach((periodId) => {
      homeRunningTotal += homeTeamQuarters["period" + periodId];
      homeTeamQuarters["totalPeriod" + periodId] = homeRunningTotal;
      awayRunningTotal += awayTeamQuarters["period" + periodId];
      awayTeamQuarters["totalPeriod" + periodId] = awayRunningTotal;
    });

    return (
      <IntlProvider locale={props.locale ?? "en-US"}>
        <Document>
          <Page size="A4" style={styles.page}>
            <QuarterScoresReportPage
              {...{
                homeTeam,
                awayTeam,
                fixture,
                homeTeamPeriods,
                awayTeamPeriods,
                homeTeamQuarters,
                awayTeamQuarters,
                periodsScores,
                periodIds,
                players,
                locale,
                language,
                intl,
                roles,
                fixtureProfile,
              }}
            />
          </Page>
        </Document>
      </IntlProvider>
    );
  } else {
    return null;
  }
};

const QuarterScoresReportPage = ({
  homeTeam,
  awayTeam,
  fixture,
  homeTeamPeriods,
  awayTeamPeriods,
  periodsScores,
  homeTeamQuarters,
  awayTeamQuarters,
  periodIds,
  players,
  locale,
  language,
  intl,
  roles,
  fixtureProfile,
}) => {
  return (
    <React.Fragment>
      <View style={{ marginBottom: "3mm" }}>
        <FixtureReportsHeader
          {...{
            homeTeam,
            awayTeam,
            fixture,
            periodsScores,
            language,
            roles,
            locale,
            title: intl.formatMessage({ id: "reports.quarterscores" }),
            intl,
          }}
        />
      </View>
      <View style={styles.section}>
        <ScoringTable
          {...{
            homeTeam,
            awayTeam,
            homeTeamPeriods,
            awayTeamPeriods,
            intl,
            fixtureProfile,
          }}
        />
      </View>
      <ReportTable
        teamQuarters={homeTeamQuarters}
        periodIds={periodIds}
        intl={intl}
        team={homeTeam}
        players={players}
        isHome={true}
      ></ReportTable>
      <ReportTable
        teamQuarters={awayTeamQuarters}
        periodIds={periodIds}
        intl={intl}
        team={awayTeam}
        players={players}
        isHome={false}
      ></ReportTable>
      <FixtureReportsFooter roles={roles} intl={intl} />
    </React.Fragment>
  );
};

const ReportTable = ({
  team,
  players,
  isHome,
  teamQuarters,
  periodIds,
  intl,
}) => {
  const teamPlayerIds = team.players.map((player) => player.personId);
  let teamPlayers = teamPlayerIds.map((personId) => players[personId]);

  teamPlayers.sort((a, b) => b.total - a.total);

  return (
    <React.Fragment>
      <Row
        style={{
          fontSize: 7,
          marginTop: "6mm",
          border: 0,
          borderBottomWidth: 0,
        }}
      >
        <TextCell left flex style={{ border: "none" }}>
          {team.name}
        </TextCell>
        <TextCell left flex style={{ border: "none" }}>
          {team.score}
        </TextCell>
      </Row>
      <Table style={{ border: "none" }}>
        <Row
          height={"0.8cm"}
          style={{
            fontSize: 7,
            backgroundColor: "#f0f0f0",
            borderRightWidth: 0,
            borderBottomWidth: 0,
          }}
        >
          <TextCell height={"0.8cm"} width={"1.5cm"}>
            {intl.formatMessage({ id: "reports.quarterscores.table.no" })}
          </TextCell>
          <TextCell height={"0.8cm"} width={"2cm"}>
            {intl.formatMessage({ id: "reports.quarterscores.table.pos" })}
          </TextCell>
          <TextCell height={"0.8cm"} flex>
            {intl.formatMessage({ id: "reports.quarterscores.table.name" })}
          </TextCell>
          <TextCell height={"0.8cm"} width={"1.5cm"}>
            {intl.formatMessage({ id: "reports.quarterscores.table.total" })}
          </TextCell>
          {periodIds.map((periodId) => {
            return (
              <TextCell key={periodId} width={"1.2cm"}>
                {periodLabel(periodId)}
              </TextCell>
            );
          })}
        </Row>
        {teamPlayers.map((player, index) => {
          return (
            <Row
              key={index}
              style={{
                fontSize: 7,
                backgroundColor: index % 2 ? "#f0f0f0" : "white",
                borderRightWidth: 0,
                borderBottomWidth: 0,
              }}
            >
              <TextCell width={"1.5cm"}>{safeString(player, "bib")}</TextCell>
              <TextCell width={"2cm"}> </TextCell>
              <TextCell left flex>
                {safeString(player, "personFullName")}
              </TextCell>
              <TextCell width={"1.5cm"}>{safeZero(player, "total")}</TextCell>
              {periodIds.map((periodId) => {
                return (
                  <TextCell key={periodId} width={"1.2cm"}>
                    {safeZero(player, "period" + periodId)}
                  </TextCell>
                );
              })}
            </Row>
          );
        })}
        <React.Fragment>
          <Row
            style={{
              fontSize: 8,
              fontWeight: "bold",
              backgroundColor: teamPlayerIds.length % 2 ? "#f0f0f0" : "white",
              borderRightWidth: 0,
              borderBottomWidth: 0,
            }}
          >
            <TextCell right flex>
              {intl.formatMessage({
                id: "reports.quarterscores.table.quarterscores",
              })}
            </TextCell>
            <TextCell width={"1.5cm"}>{teamQuarters.total}</TextCell>
            {periodIds.map((periodId) => {
              return (
                <TextCell key={periodId} width={"1.2cm"}>
                  {teamQuarters["period" + periodId]}
                </TextCell>
              );
            })}
          </Row>
          <Row
            style={{
              fontSize: 8,
              fontWeight: "bold",
              backgroundColor:
                (teamPlayerIds.length + 1) % 2 ? "#f0f0f0" : "white",
              borderRightWidth: 0,
            }}
          >
            <TextCell right flex>
              {" "}
            </TextCell>
            <TextCell width={"1.5cm"}> </TextCell>
            {periodIds.map((periodId) => {
              return (
                <TextCell key={periodId} width={"1.2cm"}>
                  {teamQuarters["totalPeriod" + periodId]}
                </TextCell>
              );
            })}
          </Row>
        </React.Fragment>
      </Table>
    </React.Fragment>
  );
};
